import { ComponentRendering } from '@sitecore-jss/sitecore-jss-react';
import React, { useEffect, useRef, useState } from 'react';
import { Carousel as ReactResponsiveCarousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { formatComponentNumber, googleClickSlideEvent } from '../../../utils/analytics';
import { documentService, windowService } from '../../../utils/window.utils';
import { getYoutubeVideoId } from '../../../utils/youtubeUtils';
import AnalyticsVisibleEvent from '../../@shared/analyticsVisibleEvent';
import CarouselTransparentNavSection from '../../@shared/carouselTransparentNavSection';
import CustomCarouselDots from '../../@shared/customCarouselDots';
import { CarouselNavPositionEnum } from '../../../enums/carousel.enums';
import useMediaQuery from '../../../lib/custom-hooks/useMediaQuery';
import GalleryCarouselArrow from '../components/galleryCarouselArrow';
import GalleryCarouselIndicator from '../components/galleryCarouselIndicator';
import GalleryCarouselSlide from '../components/galleryCarouselSlide';
import { GalleryCarouselModel } from '../models/galleryCarousel.model';

function GalleryCarousel(props: { model: GalleryCarouselModel; rendering: ComponentRendering }): JSX.Element {
  const model = props.model;
  const isDesktop = useMediaQuery('(min-width: 768px)');

  useEffect(() => {
    const height = windowService().innerHeight;
    documentService().documentElement.style.setProperty('--vh-100', `${height}px`);
    const resize = (): void => {
      const vh = windowService().innerHeight;
      documentService().documentElement.style.setProperty('--vh-100', `${vh}px`);
    };
    windowService().addEventListener('resize', resize);
    return (): void => {
      windowService().removeEventListener('resize', resize);
    };
  }, []);

  const interval = 10000; // when you change this change also the $interval variable in GalleryCarousel > style.scss

  const hasMultipleSlides = model.carouselSlide ? model.carouselSlide.length > 1 : false;

  const [currentSlide, _setCurrentSlide] = useState(0);

  const slidePos = model.carouselSlide ? model.carouselSlide[currentSlide] : null;

  const setCurrentSlide = hasMultipleSlides
    ? (index: number): void => {
        if (currentSlide !== index) {
          _setCurrentSlide(index);
        }
      }
    : (): void => {
        // This is intentional
      };

  const normalizeSlideNumber = (slide: number) => {
    if (!model?.carouselSlide?.length) {
      return slide;
    }

    return (slide + model?.carouselSlide.length) % model?.carouselSlide.length;
  };

  const goNext = (): void => {
    const slide = normalizeSlideNumber(currentSlide + 1);
    setCurrentSlide(slide);
    googleClickSlideEvent(`Slide ${formatComponentNumber(slide + 1)}`, '');
  };

  const goPrevious = (): void => {
    const slide = normalizeSlideNumber(currentSlide - 1);
    setCurrentSlide(slide);
    googleClickSlideEvent(`Slide ${formatComponentNumber(slide - 1)}`, '');
  };

  const carouselRef = useRef<any>(null);

  const findLoadedImgSrc = (): string | undefined => {
    const currentSlideDOM: HTMLDListElement = carouselRef?.current?.itemsRef?.[currentSlide];
    const image = currentSlideDOM?.querySelector?.('img');
    return image?.currentSrc;
  };

  const MediaDownload = (): void => {
    const title = '';
    const isVideo = slidePos?.fields?.useVideo?.value;
    const videoUrl = slidePos?.fields?.video?.value?.href;
    const url = (isVideo ? videoUrl : findLoadedImgSrc()) || '';

    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.responseType = 'blob';
    xhr.onload = function (): void {
      const urlCreator = windowService().URL || windowService().webkitURL;
      const imageUrlValue = urlCreator.createObjectURL(this.response);
      const tag = documentService().createElement('a');
      tag.href = imageUrlValue;
      tag.download = title;
      documentService().body.appendChild(tag);
      tag.click();
      documentService().body.removeChild(tag);
    };
    xhr.send();
  };
  const isYouTubeUrl = getYoutubeVideoId(slidePos?.fields?.video?.value?.href || '');
  const isImageUrl = slidePos?.fields?.image?.value?.src || slidePos?.fields?.mobileImage?.value?.src;
  const showDownloadIcon = slidePos?.fields?.useVideo?.value ? !isYouTubeUrl : isImageUrl;
  return (
    <AnalyticsVisibleEvent rendering={props?.rendering} title="" template="Media Gallery Full Screen">
      {model && (
        <>
          <div className="fullscreen-media-gallery__carousel">
            <ReactResponsiveCarousel
              infiniteLoop={true}
              interval={interval}
              autoPlay={true}
              onChange={setCurrentSlide}
              selectedItem={currentSlide}
              showThumbs={false}
              showArrows={false}
              showIndicators={false}
              showStatus={false}
              stopOnHover={false}
              ref={carouselRef}
              swipeable={!isDesktop}
              emulateTouch={!isDesktop}
            >
              {model?.carouselSlide?.map((itemFields, index) => {
                const slideInfo = { currentSlide, index };
                return (
                  <GalleryCarouselSlide
                    id={itemFields.id ?? itemFields.dataSource}
                    key={itemFields.id}
                    slideInfo={slideInfo}
                    fields={itemFields?.fields}
                  />
                );
              })}
            </ReactResponsiveCarousel>
            {showDownloadIcon && <GalleryCarouselArrow direction={'download'} onClick={MediaDownload} />}
            {hasMultipleSlides && (
              <div className={'controls'}>
                <GalleryCarouselArrow direction={'previous'} onClick={goPrevious} />
                <CarouselTransparentNavSection position={CarouselNavPositionEnum.LEFT} handleTouchStart={goPrevious} />

                {
                  <ul className={'controls__dots'}>
                    {(model?.carouselSlide || []).length > 5 ? (
                      <CustomCarouselDots
                        cardsCount={(model?.carouselSlide || []).length}
                        activeCard={currentSlide + 1}
                        hasAnimation={false}
                      />
                    ) : (
                      model?.carouselSlide?.map((slide, index) => (
                        <GalleryCarouselIndicator
                          baseClassName={'controls__dots__item'}
                          index={index}
                          key={'dot' + slide.id}
                          isSelected={index === currentSlide}
                          goSelected={() => {
                            // This is intentional
                          }}
                        />
                      ))
                    )}
                  </ul>
                }
                <CarouselTransparentNavSection position={CarouselNavPositionEnum.RIGHT} handleTouchStart={goNext} />

                <GalleryCarouselArrow direction={'next'} onClick={goNext} />
              </div>
            )}
          </div>
        </>
      )}
    </AnalyticsVisibleEvent>
  );
}

export default GalleryCarousel;
